import { ToastType } from "@src/components/CustomToast/types";
import { showCustomToast } from "@src/components/CustomToast/utils/show-custom-toast";
import { toast } from "@src/lib/sonner";
import { getErrorMessage } from "@src/services/api/error-message";

type ToastExtraOptions = {
  /** Toast id */
  id?: string | number;
  /** Duration in milliseconds before the toast auto-dismisses */
  duration?: number;
  /** Progress bar boolean */
  progress?: boolean;
  /** Close button boolean */
  closeButton?: boolean;
  /** Dismissible boolean */
  dismissible?: boolean;
  /** Action button configuration */
  actions?: {
    actionLabel: string;
    onAction: () => void;
    cancelLabel?: string;
    onCancel?: () => void;
  };
};

type ShowToastProps = {
  message: string;
  description?: string;
  options?: ToastExtraOptions;
};

/** Known copy, or `{ error }` and `getErrorMessage` supplies the headline. */
export type ShowErrorToastInput =
  | ShowToastProps
  | { error: unknown; description?: string; options?: ToastExtraOptions };

type ShowToastWithActionsProps = {
  type: ToastType;
  message: string;
  description?: string;
  actions?: {
    actionLabel: string;
    onAction: () => void;
    cancelLabel?: string;
    onCancel?: () => void;
  };
  options?: Omit<ToastExtraOptions, "actions">;
};

type PromiseToastMessages<T> = {
  loading: string;
  success: string | ((data: T) => string);
  error: string | ((err: unknown) => string);
};

type ShowPromiseToastProps<T> = {
  promise: Promise<T>;
  messages: PromiseToastMessages<T>;
};

export const generateIdToast = () => {
  return Math.random().toString(36).slice(2, 11);
};

/**
 * Show a success toast notification.
 * @param message - The main message to display
 * @param description - Optional description for additional context
 * @param options - Additional toast options (duration, action, cancel, closeButton, etc.)
 */
export function showSuccessToast({ message, description, options }: ShowToastProps) {
  return showCustomToast({ type: "success", message, description, ...options });
}

/**
 * Show an error toast notification.
 * @param input - The input to display. Can be a `ShowToastProps` object or an `{ error }` object.
 * @param input.message - The main message to display
 * @param input.description - Optional description for additional context
 * @param input.options - Additional toast options (duration, action, cancel, closeButton, etc.)
 * @example
 * showErrorToast({ message: "Something went wrong" });
 * showErrorToast({ error: new Error("Something went wrong") });
 */
export const showErrorToast = (input: ShowErrorToastInput) =>
  "error" in input ?
    showCustomToast({
      type: "error",
      message: getErrorMessage(input.error),
      description: input.description,
      ...input.options,
    })
  : showCustomToast({ type: "error", message: input.message, description: input.description, ...input.options });

/**
 * Show an info toast notification.
 * @param message - The main message to display
 * @param description - Optional description for additional context
 * @param options - Additional toast options (icon, iconColor, style, action, etc.)
 */
export function showInfoToast({ message, description, options }: ShowToastProps) {
  return showCustomToast({ type: "info", message, description, ...options });
}

/**
 * Show a warning toast notification.
 * @param message - The main message to display
 * @param description - Optional description for additional context
 * @param options - Additional toast options (icon, iconColor, style, action, etc.)
 */
export function showWarningToast({ message, description, options }: ShowToastProps) {
  return showCustomToast({ type: "warning", message, description, ...options });
}

/**
 * Show a toast with an action button.
 * @param message - The main message to display
 * @param description - Optional description for additional context
 * @param options - Additional toast options (duration, action, cancel, closeButton, etc.)
 */
export function showToastWithActions({ type, message, description, actions, options }: ShowToastWithActionsProps) {
  return showCustomToast({
    type,
    message,
    description,
    closeButton: options?.closeButton ?? false,
    actions: {
      actionLabel: actions?.actionLabel ?? "",
      onAction: actions?.onAction ?? (() => {}),
      cancelLabel: actions?.cancelLabel ?? "",
      onCancel: actions?.onCancel ?? (() => {}),
    },
  });
}

// Re-export the original toast for advanced usage (promise, custom, dismiss, etc.)
export { toast };

// Re-export showCustomToast so consumers can import all toast utilities from one place
export { showCustomToast } from "@src/components/CustomToast/utils/show-custom-toast";
